import React, { useMemo, useState } from 'react';
import {
  User,
  MapPin,
  Phone,
  Lock,
  Palette,
  Landmark,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import styles from './FormArtist.module.css';

const estadosBR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

const generos = ['Feminino', 'Masculino', 'Não binário', 'Prefiro não informar', 'Outro'];

const escolaridades = [
  'Ensino fundamental incompleto',
  'Ensino fundamental completo',
  'Ensino médio incompleto',
  'Ensino médio completo',
  'Ensino superior incompleto',
  'Ensino superior completo',
  'Pós-graduação',
  'Mestrado',
  'Doutorado'
];

const linguagensArtisticas = [
  'Música',
  'Teatro',
  'Dança',
  'Artes visuais',
  'Audiovisual',
  'Literatura',
  'Artesanato',
  'Artes Circenses',
  'Cultura popular',
  'Gastronomia',
  'Fotografia',
  'Pesquisa',
  'Artes Plásticas',
  'Outra'
];

const tiposConta = ['Corrente', 'Poupança', 'Pagamento', 'Salário'];

const initialState = {
  nomeCompleto: '',
  nomeSocial: '',
  nomeArtistico: '',
  cpf: '',
  dataDeNascimento: '',
  nomeDoPai: '',
  nomeDaMae: '',
  genero: '',
  escolaridade: '',
  endereco: '',
  numero: '',
  bairro: '',
  cep: '',
  cidade: '',
  estado: '',
  moradorDoMunicipio: '',
  celular: '',
  email: '',
  senha: '',
  confirmarSenha: '',
  linguagemArtistica: [],
  rendaIndividualExclusivoDoTrabalhoArtistico: '',
  outrasRendas: '',
  participaDeOrganizacao: '',
  vinculoFUNDARPE: '',
  tocouTocaNaFUNDARPE: '',
  espacoApresentacao: '',
  tempoDeAtividadeArtistica: '',
  apresentacaoPorSemanada: '',
  linksDeMaterias: '',
  linkPaginaDoArtista: '',
  linkCurriculo: null,
  fotoArtista: null,
  banco: '',
  agencia: '',
  conta: '',
  tipoDeConta: ''
};

const requiredFields = [
  'nomeCompleto',
  'cpf',
  'dataDeNascimento',
  'nomeDaMae',
  'genero',
  'escolaridade',
  'endereco',
  'numero',
  'bairro',
  'cep',
  'cidade',
  'estado',
  'moradorDoMunicipio',
  'celular',
  'email',
  'senha',
  'confirmarSenha',
  'linguagemArtistica',
  'rendaIndividualExclusivoDoTrabalhoArtistico',
  'vinculoFUNDARPE',
  'tocouTocaNaFUNDARPE',
  'tempoDeAtividadeArtistica',
  'linkCurriculo',
  'fotoArtista',
  'banco',
  'agencia',
  'conta',
  'tipoDeConta'
];

function formatCPF(value) {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function formatCEP(value) {
  return value
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function onlyNumbers(value, max = 20) {
  return value.replace(/\D/g, '').slice(0, max);
}


function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <Icon size={20} />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <p className={styles.sectionSubtitle}>{subtitle}</p>
        </div>
      </div>
      <div className={styles.sectionGrid}>{children}</div>
    </section>
  );
}

function Field({ label, required, hint, children, fullWidth = false, as: Component = 'label' }) {
  return (
    <Component className={`${styles.field} ${fullWidth ? styles.fullWidth : ''}`}>
      <div className={styles.fieldTop}>
        <span>{label}</span>
        {required && <span className={styles.requiredBadge}>Obrigatório</span>}
      </div>
      {children}
      {hint && <p className={styles.fieldHint}>{hint}</p>}
    </Component>
  );
}

function BooleanGroup({ name, value, onChange }) {
  return (
    <div className={styles.choiceGrid}>
      <button
        type="button"
        className={`${styles.choiceCard} ${value === 'true' ? styles.choiceCardActive : ''}`}
        onClick={() => onChange(name, 'true')}
      >
        Sim
      </button>
      <button
        type="button"
        className={`${styles.choiceCard} ${value === 'false' ? styles.choiceCardActive : ''}`}
        onClick={() => onChange(name, 'false')}
      >
        Não
      </button>
    </div>
  );
}

export default function ArtistRegistrationPage() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const errors = useMemo(() => {
    if (!submitted) return {};

    return requiredFields.reduce((acc, key) => {
      if (key === 'linguagemArtistica') {
        if (!Array.isArray(form.linguagemArtistica) || form.linguagemArtistica.length === 0) {
          acc[key] = true;
        }
        return acc;
      }

      if (key === 'linkCurriculo' || key === 'fotoArtista') {
        if (!form[key]) acc[key] = true;
        return acc;
      }

      if (String(form[key]).trim() === '') {
        acc[key] = true;
      }

      return acc;
    }, {});
  }, [form, submitted]);

  const completion = useMemo(() => {
    const filled = requiredFields.filter((key) => {
      if (key === 'linguagemArtistica') {
        return Array.isArray(form[key]) && form[key].length > 0;
      }

      if (key === 'linkCurriculo' || key === 'fotoArtista') {
        return !!form[key];
      }

      return form[key] !== undefined && form[key] !== null && String(form[key]).trim() !== '';
    }).length;

    return Math.round((filled / requiredFields.length) * 100);
  }, [form]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleChange(event) {
    const { name, value, type, files, options, multiple } = event.target;

    if (type === 'file') {
      return updateField(name, files?.[0] || null);
    }

    if (multiple && name === 'linguagemArtistica') {
      const selectedValues = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);
      
      return updateField(name, selectedValues);
    }

    if (name === 'cpf') return updateField(name, formatCPF(value));
    if (name === 'cep') return updateField(name, formatCEP(value));
    if (name === 'celular') return updateField(name, formatPhone(value));

    if (
      name === 'numero' ||
      name === 'agencia' ||
      name === 'conta' ||
      name === 'apresentacaoPorSemanada'
    ) {
      return updateField(name, onlyNumbers(value));
    }

    updateField(name, value);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitted(true);
    setApiError('');
    setSuccessMessage('');

    const hasErrors = requiredFields.some((key) => {
      if (key === 'linguagemArtistica') return form.linguagemArtistica.length === 0;
      if (key === 'linkCurriculo' || key === 'fotoArtista') return !form[key];
      return String(form[key]).trim() === '';
    });

    if (hasErrors) return;

    try {
      setLoading(true);

      const payload = new FormData();

      payload.append('nomeCompleto', form.nomeCompleto);
      payload.append('nomeSocial', form.nomeSocial);
      payload.append('nomeArtistico', form.nomeArtistico);
      payload.append('cpf', form.cpf);
      payload.append('dataDeNascimento', form.dataDeNascimento);
      payload.append('nomeDoPai', form.nomeDoPai);
      payload.append('nomeDaMae', form.nomeDaMae);
      payload.append('genero', form.genero);
      payload.append('escolaridade', form.escolaridade);
      payload.append('endereco', form.endereco);
      payload.append('numero', form.numero ? Number(form.numero) : '');
      payload.append('bairro', form.bairro);
      payload.append('cep', form.cep);
      payload.append('cidade', form.cidade);
      payload.append('estado', form.estado);
      payload.append('moradorDoMunicipio', form.moradorDoMunicipio === 'true');
      payload.append('celular', form.celular);
      payload.append('email', form.email);
      payload.append('senha', form.senha);
      payload.append('confirmarSenha', form.confirmarSenha);
      payload.append('linguagemArtistica', JSON.stringify(form.linguagemArtistica));
      payload.append(
        'rendaIndividualExclusivoDoTrabalhoArtistico',
        form.rendaIndividualExclusivoDoTrabalhoArtistico === 'true'
      );
      payload.append('outrasRendas', form.outrasRendas);
      payload.append('participaDeOrganizacao', form.participaDeOrganizacao);
      payload.append('vinculoFUNDARPE', form.vinculoFUNDARPE === 'true');
      payload.append('tocouTocaNaFUNDARPE', form.tocouTocaNaFUNDARPE === 'true');
      payload.append('espacoApresentacao', form.espacoApresentacao);
      payload.append('tempoDeAtividadeArtistica', form.tempoDeAtividadeArtistica);
      payload.append('apresentacaoPorSemanada', form.apresentacaoPorSemanada || '');
      payload.append('linksDeMaterias', form.linksDeMaterias);
      payload.append('linkPaginaDoArtista', form.linkPaginaDoArtista);
      payload.append('banco', form.banco);
      payload.append('agencia', form.agencia ? Number(form.agencia) : '');
      payload.append('conta', form.conta ? Number(form.conta) : '');
      payload.append('tipoDeConta', form.tipoDeConta);

      if (form.linkCurriculo) {
        payload.append('linkCurriculo', form.linkCurriculo);
      }

      if (form.fotoArtista) {
        payload.append('fotoArtista', form.fotoArtista);
      }

      const response = await axios.post('https://back-cultura-sje-production.up.railway.app/artists', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccessMessage(response.data.message || 'Cadastro realizado com sucesso.');
      setForm(initialState);
      setSubmitted(false);
    } catch (error) {
      console.error('Erro ao cadastrar artista', error);
      setApiError(
        error.response?.data?.message || 'Não foi possível enviar o cadastro.'
      );
    } finally {
      setLoading(false);
    }
  }

  function toggleLinguagemArtistica(value) {
    setForm((prev) => {
      const jaSelecionada = prev.linguagemArtistica.includes(value);

      return {
        ...prev,
        linguagemArtistica: jaSelecionada
          ? prev.linguagemArtistica.filter((item) => item !== value)
          : [...prev.linguagemArtistica, value]
      };
    });
  }

  function getInputClass(hasError) {
    return `${styles.input} ${hasError ? styles.inputError : ''}`;
  }

  function getTextareaClass(hasError) {
    return `${styles.textarea} ${hasError ? styles.inputError : ''}`;
  }

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <h1 className={styles.sidebarTitle}>Cadastro de Artista</h1>

          <div className={styles.progressCard}>
            <div className={styles.progressHeader}>
              <span>Preenchimento</span>
              <strong>{completion}%</strong>
            </div>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${completion}%` }} />
            </div>
            <p className={styles.progressText}>
              Campos obrigatórios possuem validação visual no envio.
            </p>
          </div>

          <div className={styles.sidebarList}>
            {[
              'Dados pessoais e identificação',
              'Endereço e contato',
              'Atuação artística e vínculos',
              'Informações bancárias'
            ].map((item) => (
              <div key={item} className={styles.sidebarItem}>
                <CheckCircle2 size={18} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </aside>

        <form className={styles.form} onSubmit={handleSubmit}>
          <Section
            icon={User}
            title="Dados pessoais"
            subtitle="Informações de identificação do artista para o cadastro."
          >
            <Field label="Nome completo" required>
              <input
                name="nomeCompleto"
                value={form.nomeCompleto}
                onChange={handleChange}
                className={getInputClass(errors.nomeCompleto)}
                placeholder="Digite o nome completo"
              />
            </Field>

            <Field label="Nome social">
              <input
                name="nomeSocial"
                value={form.nomeSocial}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Digite o nome social"
              />
            </Field>

            <Field label="Nome artístico">
              <input
                name="nomeArtistico"
                value={form.nomeArtistico}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Digite o nome artístico"
              />
            </Field>

            <Field label="CPF" required hint="Formatação automática.">
              <input
                name="cpf"
                value={form.cpf}
                onChange={handleChange}
                className={getInputClass(errors.cpf)}
                placeholder="000.000.000-00"
              />
            </Field>

            <Field label="Data de nascimento" required>
              <input
                type="date"
                name="dataDeNascimento"
                value={form.dataDeNascimento}
                onChange={handleChange}
                className={getInputClass(errors.dataDeNascimento)}
              />
            </Field>

            <Field label="Nome do pai">
              <input
                name="nomeDoPai"
                value={form.nomeDoPai}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Digite o nome do pai"
              />
            </Field>

            <Field label="Nome da mãe" required>
              <input
                name="nomeDaMae"
                value={form.nomeDaMae}
                onChange={handleChange}
                className={getInputClass(errors.nomeDaMae)}
                placeholder="Digite o nome da mãe"
              />
            </Field>

            <Field label="Gênero" required>
              <select
                name="genero"
                value={form.genero}
                onChange={handleChange}
                className={getInputClass(errors.genero)}
              >
                <option value="">Selecione</option>
                {generos.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Escolaridade" required>
              <select
                name="escolaridade"
                value={form.escolaridade}
                onChange={handleChange}
                className={getInputClass(errors.escolaridade)}
              >
                <option value="">Selecione</option>
                {escolaridades.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section
            icon={MapPin}
            title="Endereço e localização"
            subtitle="Dados residenciais e vínculo com o município."
          >
            <Field label="Endereço" required fullWidth>
              <input
                name="endereco"
                value={form.endereco}
                onChange={handleChange}
                className={getInputClass(errors.endereco)}
                placeholder="Rua, avenida ou logradouro"
              />
            </Field>

            <Field label="Número" required>
              <input
                name="numero"
                value={form.numero}
                onChange={handleChange}
                className={getInputClass(errors.numero)}
                placeholder="123"
              />
            </Field>

            <Field label="Bairro" required>
              <input
                name="bairro"
                value={form.bairro}
                onChange={handleChange}
                className={getInputClass(errors.bairro)}
                placeholder="Digite o bairro"
              />
            </Field>

            <Field label="CEP" required>
              <input
                name="cep"
                value={form.cep}
                onChange={handleChange}
                className={getInputClass(errors.cep)}
                placeholder="00000-000"
              />
            </Field>

            <Field label="Cidade" required>
              <input
                name="cidade"
                value={form.cidade}
                onChange={handleChange}
                className={getInputClass(errors.cidade)}
                placeholder="Digite a cidade"
              />
            </Field>

            <Field label="Estado" required>
              <select
                name="estado"
                value={form.estado}
                onChange={handleChange}
                className={getInputClass(errors.estado)}
              >
                <option value="">Selecione</option>
                {estadosBR.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Morador do município" required fullWidth as="div">
              <BooleanGroup
                name="moradorDoMunicipio"
                value={form.moradorDoMunicipio}
                onChange={updateField}
              />
            </Field>
          </Section>

          <Section
            icon={Phone}
            title="Contato e acesso"
            subtitle="Informações para comunicação e autenticação do artista."
          >
            <Field label="Celular" required>
              <input
                name="celular"
                value={form.celular}
                onChange={handleChange}
                className={getInputClass(errors.celular)}
                placeholder="(81) 99999-9999"
              />
            </Field>

            <Field label="E-mail (Use para login)" required>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={getInputClass(errors.email)}
                placeholder="nome@exemplo.com"
              />
            </Field>

            <Field label="Senha" required fullWidth>
              <div className={styles.passwordWrapper}>
                <Lock size={18} className={styles.passwordIcon} />
                <input
                  type="password"
                  name="senha"
                  value={form.senha}
                  onChange={handleChange}
                  className={getInputClass(errors.senha)}
                  placeholder="Crie uma senha segura"
                />
              </div>
            </Field>

            <Field label="Confirmar senha" required fullWidth>
              <div className={styles.passwordWrapper}>
                <Lock size={18} className={styles.passwordIcon} />
                <input
                  type="password"
                  name="confirmarSenha"
                  value={form.confirmarSenha}
                  onChange={handleChange}
                  className={getInputClass(errors.confirmarSenha)}
                  placeholder="Digite novamente a senha"
                />
              </div>
            </Field>
          </Section>

          <Section
            icon={Palette}
            title="Perfil artístico"
            subtitle="Experiência, renda, vínculos institucionais e presença digital."
          >
            <Field
              label="Linguagem artística"
              required
              fullWidth
              hint="Você pode selecionar mais de uma opção."
              as="div"
            >
              <div className={styles.multiChoiceGrid}>
                {linguagensArtisticas.map((item) => {
                  const active = form.linguagemArtistica.includes(item);

                  return (
                    <button
                      key={item}
                      type="button"
                      className={`${styles.multiChoiceCard} ${active ? styles.multiChoiceCardActive : ''}`}
                      onClick={() => toggleLinguagemArtistica(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>

              {errors.linguagemArtistica && (
                <p className={styles.fieldHint}>Selecione pelo menos uma linguagem artística.</p>
              )}
            </Field>

            <Field label="Tempo de atividade artística" required>
              <input
                name="tempoDeAtividadeArtistica"
                value={form.tempoDeAtividadeArtistica}
                onChange={handleChange}
                className={getInputClass(errors.tempoDeAtividadeArtistica)}
                placeholder="Ex.: 10 anos"
              />
            </Field>

            <Field
              label="A renda individual é exclusiva do trabalho artístico?"
              required
              fullWidth
              as="div"
            >
              <BooleanGroup
                name="rendaIndividualExclusivoDoTrabalhoArtistico"
                value={form.rendaIndividualExclusivoDoTrabalhoArtistico}
                onChange={updateField}
              />
            </Field>

            <Field label="Outras rendas">
              <input
                name="outrasRendas"
                value={form.outrasRendas}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Descreva outras fontes de renda"
              />
            </Field>

            <Field label="Participa de organização?">
              <input
                name="participaDeOrganizacao"
                value={form.participaDeOrganizacao}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Informe organização, coletivo ou associação"
              />
            </Field>

            <Field label="Possui vínculo com a FUNDARPE?" required as="div">
              <BooleanGroup
                name="vinculoFUNDARPE"
                value={form.vinculoFUNDARPE}
                onChange={updateField}
              />
            </Field>

            <Field label="Já tocou ou toca na FUNDARPE?" required as="div">
              <BooleanGroup
                name="tocouTocaNaFUNDARPE"
                value={form.tocouTocaNaFUNDARPE}
                onChange={updateField}
              />
            </Field>

            <Field label="Espaço de apresentação">
              <input
                name="espacoApresentacao"
                value={form.espacoApresentacao}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="Informe os principais espaços de apresentação"
              />
            </Field>

            <Field label="Apresentações por semana">
              <input
                name="apresentacaoPorSemanada"
                value={form.apresentacaoPorSemanada}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="0"
              />
            </Field>

            <Field label="Links de matérias" fullWidth>
              <textarea
                name="linksDeMaterias"
                value={form.linksDeMaterias}
                onChange={handleChange}
                className={getTextareaClass()}
                placeholder="Cole aqui links de matérias, reportagens ou entrevistas"
              />
            </Field>

            <Field label="Página do artista">
              <input
                name="linkPaginaDoArtista"
                value={form.linkPaginaDoArtista}
                onChange={handleChange}
                className={getInputClass()}
                placeholder="https://"
              />
            </Field>

            <Field label="Currículo cultural" required>
              <input
                type="file"
                name="linkCurriculo"
                accept=".pdf,.doc,.docx"
                onChange={handleChange}
                className={getInputClass(errors.linkCurriculo)}
              />
            </Field>

            <Field label="Foto do artista" required>
              <input
                type="file"
                name="fotoArtista"
                accept="image/*"
                onChange={handleChange}
                className={getInputClass()}
              />
            </Field>
          </Section>

          <Section
            icon={Landmark}
            title="Dados bancários"
            subtitle="Informações para pagamento e repasse financeiro."
          >
            <Field label="Banco" required>
              <input
                name="banco"
                value={form.banco}
                onChange={handleChange}
                className={getInputClass(errors.banco)}
                placeholder="Nome do banco"
              />
            </Field>

            <Field label="Agência" required>
              <input
                name="agencia"
                value={form.agencia}
                onChange={handleChange}
                className={getInputClass(errors.agencia)}
                placeholder="0000"
              />
            </Field>

            <Field label="Conta" required>
              <input
                name="conta"
                value={form.conta}
                onChange={handleChange}
                className={getInputClass(errors.conta)}
                placeholder="Número da conta"
              />
            </Field>

            <Field label="Tipo de conta" required>
              <select
                name="tipoDeConta"
                value={form.tipoDeConta}
                onChange={handleChange}
                className={getInputClass(errors.tipoDeConta)}
              >
                <option value="">Selecione</option>
                {tiposConta.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          {apiError && <div className={styles.errorMessage}>{apiError}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                setForm(initialState);
                setSubmitted(false);
                setApiError('');
                setSuccessMessage('');
              }}
            >
              Limpar formulário
            </button>

            <button type="submit" className={styles.primaryButton} disabled={loading}>
              {loading ? 'Enviando...' : 'Finalizar cadastro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}